import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit
} from "@angular/core";
import {first, Subscription} from "rxjs";
import {Router} from "@angular/router";
import {FUIAlertActions, FUIAlertService, FUIBreakpoints, FUIResponsiveService} from "@fedeltapointofsale/ui";
import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {FilterMembers, FilterModel, provideFilterModel} from "../../../framework/bi/filter/filter.model";
import {
    ConfigurationService,
    EntityModelStatus,
    provideEntityModel,
    provideReportModel
} from "@fedeltapointofsale/data-client";
import {PivotGridEntityModel} from "../pivot-grid-entity.model";
import {BIService} from "../../../framework/bi/bi.service";
import {EditPageComponent} from "../../../framework/entity/edit/edit-page/edit-page.component";
import {CubeDefinitionData} from "../../../framework/bi/cube-definition.data";
import {PivotGridModel} from "../pivot-grid.model";
import {BIAttribute} from "../../draggable-attributes-list/bi-attribute";
import {EditPageActionEvent} from "../../../framework/entity/edit/edit-page/edit-page-action.event";
import {MdxMemberUtil} from "../../../framework/bi/mdx-member-util";
import {PivotGridReportModel} from "../pivot-grid-report.model";
import {Config, ServerAuth} from "../../../config";
import {ServerStatus, ServerStatusData} from "../../../framework/bi/server-status/server-status.data";
import {ServerStatusModel} from "../../../framework/bi/server-status/server-status.model";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: "bi-pivot-grid-edit-page",
    templateUrl: "./pivot-grid-edit-page.component.html",
    styleUrls: ["./pivot-grid-edit-page.component.scss"],
    providers: [BIService,
        provideReportModel(PivotGridReportModel),
        PivotGridModel,
        provideFilterModel(),
        provideEntityModel(PivotGridEntityModel),
        ServerStatusModel],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PivotGridEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {
    /**
     * Offset of the selection control
     */
    private static readonly WIDTH_OFFSET: number = 590;
    private static readonly HEIGHT_OFFSET: number = 390;

    exitRoute: string;

    private subscriptions: Subscription = new Subscription();

    cubeDefinition: CubeDefinitionData;
    cubeName: string;

    attributes: BIAttribute[];
    filteredAttributes: BIAttribute[];

    mandatoryAttribute: BIAttribute;
    mandatoryAttributeFieldMessage: string;
    mandatoryAttributeFilterMessage: string;


    columnAttributes: BIAttribute[] = [];
    rowAttributes: BIAttribute[] = [];
    measureAttributes: BIAttribute[] = [];
    filterAttributes: BIAttribute[] = [];

    allSelectedFilterMembers: string[];
    filterSelectedFilterMembers: string[];

    /**
     * Grid size which is dynamic based on screen size and active controls
     */
    public gridWidth: number;
    public gridHeight: number;

    public fullEdit: boolean;
    public canPublish: boolean = false;

    constructor(public pivotGridModel: PivotGridModel,
                private biService: BIService,
                public filterModel: FilterModel,
                public model: PivotGridEntityModel,
                private router: Router,
                private translateService: TranslateService,
                responsiveService: FUIResponsiveService,
                private alertService: FUIAlertService,
                private element: ElementRef,
                private ngZone: NgZone,
                private configService: ConfigurationService,
                public serverStatusModel: ServerStatusModel,
                changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef, responsiveService);

        // enable dragover on this page
        this.ngZone.runOutsideAngular(() => {
            this.element.nativeElement.addEventListener("dragover", (dragEvent: DragEvent) => {
                dragEvent.stopPropagation();
            });
        });

        this.fullEdit = this.responsiveService.breakpointApplies(FUIBreakpoints.mediumUp);
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => {
                this.fullEdit = this.responsiveService.breakpointApplies(FUIBreakpoints.mediumUp);
                this.changeDetectorRef.markForCheck();
            }
        ));

        this.configService.getBoolean(ServerAuth.PUBLISH_BI_DASHBOARD).then((canPublish: boolean) => {
            this.canPublish = canPublish;
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnInit(): void {
        this.configService.getBoolean(Config.BI_ENABLED).then(biEnabled => {
            if (!biEnabled) {
                this.router.navigate(["/main/home"], {replaceUrl: true});
            }
        });

        this.updateGridSize();

        this.subscriptions.add(this.filterModel.filterMembersUpdated$.subscribe((filterMembers: FilterMembers) => {
            if (filterMembers) {
                this.pivotGridModel.updateSlicer(filterMembers.members, filterMembers.dateRange);

                this.evaluateSelectedFilterMembers();
            }
        }));

        this.subscriptions.add(this.responsiveService.window$.subscribe((window) => {
            this.updateGridSize();
        }));

        this.model.$data.pipe(first()).subscribe(() => {
            if (this.model.status === EntityModelStatus.INSERT) {
                this.exitRoute = EditPageComponent.EXIT_ROUTE_BACK;
                this.model.data.cubeName = this.biService.getDefaultCube()
                this.cubeName = this.model.data.cubeName;
                this.init(this.model.data.cubeName);
            } else {
                this.exitRoute = "pivot/" + this.model.data.id;
                this.cubeName = this.model.data.cubeName;
                this.init(this.model.data.cubeName);
            }
        });
    }

    private evaluateSelectedFilterMembers(): void {
        this.allSelectedFilterMembers = this.filterModel.getFilterMembers();
        this.filterSelectedFilterMembers = this.allSelectedFilterMembers.filter(value => {
            value = MdxMemberUtil.getDimension(value);
            return !this.columnAttributes.some(value1 => value1.id === value) &&
                !this.rowAttributes.some(value1 => value1.id === value) && !this.measureAttributes.some(value1 => value1.id === value);
        });
    }

    init(cubeName: string): void {

        this.subscriptions.add(this.serverStatusModel.statusUpdate$.subscribe((status: ServerStatusData) => {
            if (status.status === ServerStatus.INITIALISED) {
                this.loadCube(cubeName);
            } else {
                console.log("BI Server status: " + status.status);
                console.log(status.percentComplete + "% complete");
            }
            this.changeDetectorRef.markForCheck();
        }));
    }

    loadCube(cubeName: string): void {
        this.biService.getCube(cubeName).then((cubeDefinition) => {
            this.cubeDefinition = cubeDefinition;

            this.attributes = BIAttribute.buildAttributesFromCube(cubeDefinition).sort((a, b) => this.translateService.instant(a.label).localeCompare(this.translateService.instant(b.label)));

            this.mandatoryAttribute = this.attributes.find(att => att.mandatory);
            if (this.mandatoryAttribute) {
                this.mandatoryAttributeFieldMessage = this.translateService.instant("wbo.bi-pivot-grid.edit.field-set.mandatory.field", {"attribute": this.translateService.instant(this.mandatoryAttribute.label)});
                this.mandatoryAttributeFilterMessage = this.translateService.instant("wbo.bi-pivot-grid.edit.field-set.mandatory.filter", {"attribute": this.translateService.instant(this.mandatoryAttribute.label)});
            } else {
                this.mandatoryAttributeFieldMessage = undefined;
                this.mandatoryAttributeFilterMessage = undefined;
            }

            if (this.model.data.columns.length) {
                this.columnAttributes = this.getAttributesForMembers(this.model.data.columns[0].dimensions);
                this.columnAttributes.forEach(value => value.used = true);
            }
            if (this.model.data.rows.length) {
                this.rowAttributes = this.getAttributesForMembers(this.model.data.rows[0].dimensions);
                this.rowAttributes.forEach(value => value.used = true);
            }
            this.measureAttributes = this.getAttributesForMembers(this.model.data.columnMeasures);
            this.measureAttributes.forEach(value => value.used = true);

            this.filterAttributes = this.getAttributesForMembers(this.model.data.slicers)
                .filter(value => !this.columnAttributes.some(value1 => value1.id === value.id) &&
                    !this.rowAttributes.some(value1 => value1.id === value.id) && !this.measureAttributes.some(value1 => value1.id === value.id));
            this.filterAttributes.forEach(value => value.used = true);

            this.updateFilteredAttributes();

            this.pivotGridModel.init(this.cubeDefinition, this.model.data, this.reportGenerationService);

            this.filterModel.init(this.cubeDefinition, this.model.data.dateRangeRef, this.model.data.slicers);

            this.changeDetectorRef.markForCheck();
        });
    }


    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onChangeCubeName(): void {
        if (this.hasPivotCriteria()) {
            this.alertService.showConfirmationOkCancel("wbo.bi-dashboard.prompt.changeCube.heading", "wbo.bi-dashboard.prompt.changeCube.message").subscribe((action) => {
                if (action === FUIAlertActions.OK_ACTION) {
                    this.model.data.cubeName = this.cubeName;
                    this.columnAttributes = [];
                    this.rowAttributes = [];
                    this.measureAttributes = [];
                    this.filterAttributes = [];
                    this.attributes = [];
                    this.filteredAttributes = [];
                    this.allSelectedFilterMembers = [];
                    this.filterSelectedFilterMembers = [];
                    this.filterModel.initFilterMembers([]);

                    this.model.data.columnMeasures = [];
                    this.model.data.columns = [];
                    this.model.data.rowMeasures = [];
                    this.model.data.rows = [];
                    this.model.data.slicers = [];
                    this.model.data.dateRangeRef = undefined;

                    this.init(this.model.data.cubeName);
                } else {
                    this.cubeName = this.model.data.cubeName;
                    this.changeDetectorRef.markForCheck();
                }
            });
        } else {
            this.model.data.cubeName = this.cubeName;
            this.init(this.model.data.cubeName);
        }
    }

    hasPivotCriteria(): boolean {
        return this.columnAttributes.length != 0
            || this.rowAttributes.length != 0
            || this.measureAttributes.length != 0
            || this.filterAttributes.length != 0;
    }

    processChanges(items: string[]): string[][] {
        const cols: string[][] = [];

        if (items.length !== 0) {
            cols.push(items);
            if (this.pivotGridModel.openAllDimensions) {
                // Open up dimensions
                for (let i = 0; i < items.length; i++) {
                    const clone: string[] = items.slice(0);
                    for (let j = 0; j < clone.length; j++) {
                        if (j <= i) {
                            clone[j] = clone[j] + ".children";
                        }
                    }
                    cols.push(clone);
                }
            } else {
                // Open first dimension
                const clone: string[] = items.slice(0);
                clone[0] = clone[0] + ".children";
                cols.push(clone);
            }
        }
        return cols;
    }

    /**
     * Sets the grid size based on the components around it
     */
    private updateGridSize(): void {
        this.gridWidth = this.responsiveService.host().innerWidth - PivotGridEditPageComponent.WIDTH_OFFSET;
        this.gridHeight = this.responsiveService.host().innerHeight - PivotGridEditPageComponent.HEIGHT_OFFSET;
    }

    public addColumnAt(item: BIAttribute, i: number): void {
        const rowSize = this.rowAttributes.length;

        this.addBIAttributeAt(item, i, this.columnAttributes);

        this.updatePivotGridModelColumns();
        if (rowSize !== this.rowAttributes.length) {
            this.updatePivotGridModelRows();
        }
        const idx = this.filterAttributes.indexOf(item);
        let updated: boolean = false;
        if (idx !== -1) {
            updated = this.removeFilteredMember(item.value);
            this.filterAttributes.splice(idx, 1);
        }
        if (!updated) {
            this.pivotGridModel.refresh();
        }
    }

    public removeColumn(i: number, filterId: string): void {
        const item: BIAttribute = this.columnAttributes[i];
        this.columnAttributes.splice(i, 1);
        item.used = false;

        this.updatePivotGridModelColumns();
        if (!this.removeFilteredMember(filterId)) {
            this.pivotGridModel.refresh();
        }

        this.updateFilteredAttributes();
    }

    public addRowAt(item: BIAttribute, i: number): void {
        const colSize = this.columnAttributes.length;

        this.addBIAttributeAt(item, i, this.rowAttributes);
        this.updatePivotGridModelRows();
        if (colSize !== this.columnAttributes.length) {
            this.updatePivotGridModelColumns();
        }

        const idx = this.filterAttributes.indexOf(item);
        let updated: boolean = false;
        if (idx !== -1) {
            updated = this.removeFilteredMember(item.value);
            this.filterAttributes.splice(idx, 1);
        }

        if (!updated) {
            this.pivotGridModel.refresh();
        }
    }

    public removeRow(i: number, filterId: string): void {
        const item: BIAttribute = this.rowAttributes[i];
        this.rowAttributes.splice(i, 1);
        item.used = false;

        this.updatePivotGridModelRows();

        if (!this.removeFilteredMember(filterId)) {
            this.pivotGridModel.refresh();
        }

        this.updateFilteredAttributes();
    }

    public addMeasureAt(item: BIAttribute, i: number): void {
        this.addBIAttributeAt(item, i, this.measureAttributes);

        this.updatePivotGridModelMeasures();

        this.pivotGridModel.refresh();
    }

    public removeMeasure(i: number): void {
        const item: BIAttribute = this.measureAttributes[i];
        this.measureAttributes.splice(i, 1);
        item.used = false;

        this.updatePivotGridModelMeasures();

        this.pivotGridModel.refresh();

        this.updateFilteredAttributes();
    }

    public addFilterAt(item: BIAttribute, i: number): void {
        const colSize = this.columnAttributes.length;
        const rowSize = this.rowAttributes.length;

        this.addBIAttributeAt(item, i, this.filterAttributes);

        let axisChanged: boolean = false;
        if (rowSize !== this.rowAttributes.length) {
            axisChanged = true;
            this.updatePivotGridModelRows();
        }

        if (colSize !== this.columnAttributes.length) {
            axisChanged = true;
            this.updatePivotGridModelColumns();
        }

        if (axisChanged) {
            this.pivotGridModel.refresh();
        }

        this.evaluateSelectedFilterMembers();
    }

    public removeFilter(i: number, filterId: string): void {
        const item: BIAttribute = this.filterAttributes[i];
        this.filterAttributes.splice(i, 1);
        item.used = false;

        if (!this.removeFilteredMember(filterId)) {
            this.pivotGridModel.refresh();
        }

        this.updateFilteredAttributes();
    }

    private updatePivotGridModelColumns(): void {
        this.pivotGridModel.updateColumns(this.processChanges(this.columnAttributes.map(item => item.value)));
    }

    private updatePivotGridModelRows(): void {
        this.pivotGridModel.updateRows(this.processChanges(this.rowAttributes.map(item => item.value)));
    }

    private updatePivotGridModelMeasures(): void {
        this.pivotGridModel.updateMeasures(this.measureAttributes.map(item => item.value));
    }

    private removeFilteredMember(removedFilterId: string): boolean {
        if (removedFilterId && this.filterModel.filter[removedFilterId]) {
            this.filterModel.setFilterValue(removedFilterId, undefined);
            return true;
        }
        return false;
    }

    private addBIAttributeAt(item: BIAttribute, i: number, list: BIAttribute[]): void {
        item.used = true;

        const itemAt: BIAttribute = list[i];
        if (item === itemAt) {
            return;
        }

        let idx = list.indexOf(item);
        const ahead: boolean = idx === -1 || idx < i;

        this.remove(item);

        idx = list.indexOf(itemAt);
        if (ahead) {
            idx++;
        }

        list.splice(idx, 0, item);

        this.updateFilteredAttributes();
    }

    private remove(item: BIAttribute) {
        let idx = this.columnAttributes.indexOf(item);
        if (idx !== -1) {
            this.columnAttributes.splice(idx, 1);
        }
        idx = this.rowAttributes.indexOf(item);
        if (idx !== -1) {
            this.rowAttributes.splice(idx, 1);
        }
        idx = this.measureAttributes.indexOf(item);
        if (idx !== -1) {
            this.measureAttributes.splice(idx, 1);
        }
    }

    getAttributesForMembers(members: string[]): BIAttribute[] {
        return this.attributes.filter(attribute => members.some(member => member.startsWith(attribute.value)));
    }

    updateFilteredAttributes(): void {
        this.filteredAttributes = this.attributes.filter((attribute) => !attribute.used);
    }

    selectFilterMembers(attribute: BIAttribute, filterMembers: string[]): void {
        this.filterModel.setDimensionFilterMembers(attribute.id, filterMembers);
    }

    get pivotGridDateRange(): string {
        return this.model.data.dateRangeRef;
    }

    set pivotGridDateRange(dateRange: string) {
        if (dateRange !== this.model.data.dateRangeRef) {
            this.model.data.dateRangeRef = dateRange;
            this.filterModel.setFilterValue(FilterModel.DATE_RANGE_FILTER_NAME, dateRange);
        }
    }

    saved(): void {
        this.exitRoute = "pivot/" + this.model.data.id;
        this.editPageComponent.exitRoute = "pivot/" + this.model.data.id;
    }

    onSave(event: EditPageActionEvent): void {
        this.model.data.cubeName = this.cubeDefinition.cubeName;
        this.model.data.slicers = this.filterModel.getFilterMembers();
        event.actionComplete = Promise.resolve(true);
    }

    refresh(): void {
        this.filterModel.notifyFilterChange();
    }

    isMandatoryAttributeSelected(): boolean {
        if (!this.mandatoryAttribute) {
            return true;
        }

        const mandatoryAttributeFilters = this.filterModel.getFilterMembersByDimension(this.mandatoryAttribute.id);
        if (mandatoryAttributeFilters && mandatoryAttributeFilters.length == 1) {
            return true;
        }

        if (this.rowAttributes.length > 0 && this.rowAttributes[0].mandatory) {
            return true;
        }

        if (this.columnAttributes.length > 0 && this.columnAttributes[0].mandatory) {
            return true;
        }
        return false;
    }
}
